import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  useApi,
  ApiError,
  type AuditEventRow,
  type AuditEventType,
  type AuditListResponse,
} from '../api/client';
import { useCurrentUser } from '../api/useCurrentUser';
import './AuditLog.css';
import { formatDateTime } from '../api/formatDate';

/**
 * /admin/audit — viewer for dbo.audit_event.
 *
 * Administrator+ only (server-side check + client-side gate). Date range
 * defaults to the last 7 days. Event-type chips toggle which event_types
 * are included. Paginated via Next/Previous; offset-based for simplicity.
 *
 * Phase 1 simplification:
 *   • Filter by actor_oid / whanau_id is via the URL only (no UI filter).
 *     UI controls for these can land in Phase 1.5.
 *   • Detail is rendered as collapsed JSON; expanded on row click.
 *
 * Why this exists: the role-override and assessment-save endpoints have
 * been writing 'whanau.viewed.override' / 'assessment.recorded.override'
 * rows since last night. Without a UI surface, those audit signals were
 * locked behind SSMS. This page surfaces them as a real compliance view.
 */

const EVENT_TYPES: readonly AuditEventType[] = [
  'whanau.viewed.override',
  'assessment.recorded',
  'assessment.recorded.override',
  'role.assigned',
  'role.revoked',
  'exess.sync.imported',
  'keyworker.assigned',
  'keyworker.ended',
  'signoff.requested',
  'signoff.approved',
  'signoff.returned',
  'export.csv',
  'export.pdf',
];

const PAGE_SIZE = 50;

type Range = '24h' | '7d' | '30d';

function rangeToFrom(range: Range): Date {
  const now = new Date();
  switch (range) {
    case '24h':
      return new Date(now.getTime() - 24 * 60 * 60 * 1000);
    case '7d':
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    case '30d':
      return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  }
}

export function AuditLog() {
  const { t } = useTranslation();
  const callApi = useApi();
  const currentUser = useCurrentUser();

  const [range, setRange] = useState<Range>('7d');
  const [selectedTypes, setSelectedTypes] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);

  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'ok'; data: AuditListResponse }
    | { kind: 'forbidden' }
    | { kind: 'error'; message: string; status?: number }
  >({ kind: 'loading' });

  const fetchPage = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const from = rangeToFrom(range).toISOString();
      const to = new Date().toISOString();
      const params = new URLSearchParams({
        from,
        to,
        limit: String(PAGE_SIZE),
        offset: String(offset),
      });
      for (const t of selectedTypes) params.append('event_type', t);

      const data = await callApi<AuditListResponse>(
        `/admin/audit?${params.toString()}`,
      );
      setState({ kind: 'ok', data });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          setState({ kind: 'forbidden' });
        } else {
          setState({ kind: 'error', status: err.status, message: err.message });
        }
      } else {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setState({ kind: 'error', message });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, selectedTypes, offset]);

  useEffect(() => {
    void fetchPage();
  }, [fetchPage]);

  const toggleType = (type: string) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
    setOffset(0);
  };

  const onRangeChange = (r: Range) => {
    setRange(r);
    setOffset(0);
  };

  const total = state.kind === 'ok' ? state.data.total : 0;
  const showing = state.kind === 'ok' ? state.data.rows.length : 0;
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  // Client-side gate (UX only — server is authoritative).
  const isAdminOrUp =
    currentUser.kind === 'ok' &&
    (currentUser.data.role.roleId === 'administrator' ||
      currentUser.data.role.roleId === 'super-admin');

  if (currentUser.kind === 'ok' && !isAdminOrUp) {
    return (
      <article className="audit-log" aria-labelledby="audit-title">
        <p className="audit-log__status audit-log__status--error">
          {t('audit.forbidden')}
        </p>
      </article>
    );
  }

  return (
    <article className="audit-log" aria-labelledby="audit-title">
      <header className="audit-log__header">
        <h2 id="audit-title" className="audit-log__title">
          {t('audit.title')}
        </h2>
        <p className="audit-log__subtitle">{t('audit.subtitle')}</p>
      </header>

      {/* Date range tabs */}
      <div className="audit-log__range" role="tablist" aria-label={t('audit.rangeLabel')}>
        {(['24h', '7d', '30d'] as const).map((r) => (
          <button
            key={r}
            type="button"
            role="tab"
            aria-selected={range === r}
            className={
              range === r
                ? 'audit-log__range-button audit-log__range-button--active'
                : 'audit-log__range-button'
            }
            onClick={() => onRangeChange(r)}
          >
            {t(`audit.range.${r}`)}
          </button>
        ))}
      </div>

      {/* Event type chip filter */}
      <div className="audit-log__chips" role="group" aria-label={t('audit.eventTypeFilter')}>
        {EVENT_TYPES.map((type) => {
          const selected = selectedTypes.has(type);
          return (
            <button
              key={type}
              type="button"
              aria-pressed={selected}
              className={
                selected
                  ? 'audit-log__chip audit-log__chip--selected'
                  : 'audit-log__chip'
              }
              onClick={() => toggleType(type)}
            >
              {t(`audit.eventType.${type}`, { defaultValue: type })}
            </button>
          );
        })}
        {selectedTypes.size > 0 && (
          <button
            type="button"
            className="audit-log__chip-clear"
            onClick={() => {
              setSelectedTypes(new Set());
              setOffset(0);
            }}
          >
            {t('audit.clearFilter')}
          </button>
        )}
      </div>

      {/* Status */}
      {state.kind === 'loading' && (
        <p className="audit-log__status">{t('audit.loading')}</p>
      )}

      {state.kind === 'forbidden' && (
        <p className="audit-log__status audit-log__status--error">
          {t('audit.forbidden')}
        </p>
      )}

      {state.kind === 'error' && (
        <p className="audit-log__status audit-log__status--error">
          {state.status
            ? `${String(state.status)} — ${state.message}`
            : state.message}
        </p>
      )}

      {state.kind === 'ok' && state.data.total === 0 && (
        <p className="audit-log__status">{t('audit.noResults')}</p>
      )}

      {/* Results table */}
      {state.kind === 'ok' && state.data.rows.length > 0 && (
        <>
          <p className="audit-log__count">
            {t('audit.showingCount', {
              start: offset + 1,
              end: offset + showing,
              total,
            })}
          </p>
          <ul className="audit-list">
            {state.data.rows.map((row) => (
              <AuditRow
                key={row.event_id}
                row={row}
                expanded={expanded === row.event_id}
                onToggle={() =>
                  setExpanded((cur) =>
                    cur === row.event_id ? null : row.event_id,
                  )
                }
              />
            ))}
          </ul>

          <div className="audit-log__pagination">
            <button
              type="button"
              className="auth-button auth-button--secondary"
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              disabled={!canPrev}
            >
              ← {t('audit.previous')}
            </button>
            <button
              type="button"
              className="auth-button auth-button--secondary"
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              disabled={!canNext}
            >
              {t('audit.next')} →
            </button>
          </div>
        </>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Audit row — one event with expand/collapse for detail JSON.
// ---------------------------------------------------------------------------
type AuditRowProps = {
  row: AuditEventRow;
  expanded: boolean;
  onToggle: () => void;
};

function AuditRow({ row, expanded, onToggle }: AuditRowProps) {
  const { t } = useTranslation();
  const occurredAt = useMemo(
    () => formatDateTime(row.occurred_at),
    [row.occurred_at],
  );

  const isOverride = row.event_type.endsWith('.override');

  return (
    <li
      className={
        isOverride ? 'audit-row audit-row--override' : 'audit-row'
      }
    >
      <button
        type="button"
        className="audit-row__head"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span className="audit-row__time">{occurredAt}</span>
        <span className="audit-row__event">
          {t(`audit.eventType.${row.event_type}`, { defaultValue: row.event_type })}
        </span>
        {row.whanau_id !== null && (
          <span className="audit-row__whanau">
            <Link
              to={`/whanau/${String(row.whanau_id)}`}
              onClick={(e) => e.stopPropagation()}
            >
              #{row.whanau_id}
            </Link>
          </span>
        )}
        <code className="audit-row__actor">{row.actor_oid.slice(0, 8)}…</code>
        <span className="audit-row__expand" aria-hidden="true">
          {expanded ? '▼' : '▶'}
        </span>
      </button>

      {expanded && (
        <div className="audit-row__body">
          <dl className="audit-row__detail">
            <dt>{t('audit.actorFull')}</dt>
            <dd>
              <code>{row.actor_oid}</code>
            </dd>
            {row.correlation_id && (
              <>
                <dt>{t('audit.correlation')}</dt>
                <dd>
                  <code>{row.correlation_id}</code>
                </dd>
              </>
            )}
            <dt>{t('audit.detailLabel')}</dt>
            <dd>
              <pre className="audit-row__json">
                {row.detail
                  ? JSON.stringify(row.detail, null, 2)
                  : t('audit.noDetail')}
              </pre>
            </dd>
          </dl>
        </div>
      )}
    </li>
  );
}

export default AuditLog;
